/**
 * One decision point for rendering money in the bids UI (P4).
 *
 * Two distinct reasons a figure must not appear as a number:
 *
 * 1. **The caller is financially blind.** `strip_financials` already removed it server-side, so
 *    there is nothing to show. Hiding here is COSMETIC — the server is the boundary — but without
 *    it the UI renders a column of `$0`s, which is worse than an honest dash.
 * 2. **The value is absent.** The API OMITS money for a blind role rather than zeroing it (an
 *    absent key cannot be misread; a `$0` can). Formatting an absent figure as `$0` would state a
 *    falsehood — that the bid is worth nothing — which is exactly what the server-side choice to
 *    remove rather than zero exists to avoid.
 *
 * Percentages count as money: `target_margin` and `profit_pct` are stripped server-side too, so
 * `percent()` gets the same treatment. Without it the UI showed an honest `—` for the dollars and a
 * fabricated `0% margin` right beside it.
 *
 * REACTIVE by design. An earlier version kept the flag in plain module state, so setting it
 * scheduled no re-render: entitled users' money stayed redacted until something unrelated
 * re-rendered, and correctness depended on two page roots each remembering to call a hook whose
 * value they discarded. It is now a `useSyncExternalStore` source (the pattern `bidStore` already
 * uses), so anything that formats money re-renders when visibility resolves.
 */
import { useSyncExternalStore } from 'react';

/** Shown in place of a figure the caller may not see, or that the server did not send. */
export const REDACTED = '—';

// Starts REDACTED until the capability resolves.
//
// Starting "visible" looked safe — a blind caller's figures are absent, so rule 2 would redact them
// anyway — but it is not: `bidService.apiToWorkbook` coerces an absent line `rate` to 0, and the
// detail page recomputes totals CLIENT-side from those. A blind viewer would have seen "$0" totals
// until the capability fetch landed: the same falsehood rule 2 exists to prevent.
//
// So everyone sees "—" for one fetch rather than anyone seeing a fabricated figure. Failing to
// redaction is also the right direction for a confidentiality feature.
let financialVisible = false;

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setFinancialVisible(visible: boolean): void {
  if (visible === financialVisible) return;
  financialVisible = visible;
  listeners.forEach((l) => l());
}

export function isFinancialVisible(): boolean {
  return financialVisible;
}

/**
 * Subscribe a component to financial visibility. Call it in anything that formats money — the
 * returned boolean is usually ignored; what matters is the re-render when it flips.
 */
export function useFinancialVisible(): boolean {
  return useSyncExternalStore(subscribe, isFinancialVisible, () => false);
}

function hidden(value: number | null | undefined): boolean {
  return !financialVisible || value === null || value === undefined || !Number.isFinite(value);
}

/**
 * Format a dollar figure, or `REDACTED` when it must not be shown.
 *
 * A negative renders as `-$45,000`, not `$-45,000`. Deductive amounts became reachable when
 * change orders started carrying credit lines (the reversing change order that takes scope back
 * out of an additive baseline), and this formatter feeds the client-facing Notice of Change
 * letter — where a misplaced sign after the currency symbol reads as a typo on a document that
 * goes to the client. Rounding is applied before the sign test so a value like -0.4 renders `$0`
 * rather than a bare `-$0`.
 */
export function money(value: number | null | undefined): string {
  if (hidden(value)) return REDACTED;
  const rounded = Math.round(value as number);
  const magnitude = `$${Math.abs(rounded).toLocaleString('en-US')}`;
  return rounded < 0 ? `-${magnitude}` : magnitude;
}

/** Compact variant for dashboard tiles ($1.20M / $45k). Signs like money(): `-$45k`, never
 * `$-45k` — the same deductive amounts that made money() sign-aware reach the compact tiles the
 * first time a plan or exposure figure goes net-negative after a reversal. */
export function moneyCompact(value: number | null | undefined): string {
  if (hidden(value)) return REDACTED;
  const v = value as number;
  const sign = v < 0 ? '-' : '';
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${sign}$${Math.round(a / 1000)}k`;
  const rounded = Math.round(a);
  return `${rounded === 0 ? '' : sign}$${rounded.toLocaleString('en-US')}`;
}

/**
 * A margin/markup percentage. Stripped server-side exactly like a dollar figure, so it redacts on
 * the same rules — a fabricated `0% margin` beside a redacted total is the falsehood this prevents.
 *
 * `scale` converts a stored fraction (target_margin 0.18) to percent; profit_pct is already 0-100.
 */
export function percent(value: number | null | undefined, scale = 1): string {
  if (hidden(value)) return REDACTED;
  return `${Math.round((value as number) * scale)}%`;
}
